import classNames from 'classnames';
import { useEffect, useState, useRef, memo } from 'react';
import { ApiError } from '@Comments/openapi/client/index';
import { triggerEvent } from '@fec/assets/js/utils/event';

import { COMMENTS_TRIGGER_LOGIN } from '@default/webview/js/classes/SrfCommentsEvents';
import { SubmitButton } from '@Comments/components/ReplyForm/SubmitButton';
import { ConsentText } from '@Comments/components/ReplyForm/ConsentText';
import { SuccessMessage } from '@Comments/components/ReplyForm/SuccessMessage';
import {
  ErrorMessage,
  SrgLoginNotSupportedErrorMessage,
} from '@Comments/components/ReplyForm/ErrorMessage';
import {
  useLoader,
  getServerUiMessage,
} from '@Comments/services/openapiClient';
import { useAppDispatcher } from '@Comments/context/AppContext';

import './ReplyForm.scss';

// note: align duration with CSS animation duration
const SUCCESS_MESSAGE_DISPLAY_DURATION = 3500;
const MAX_COMMENT_LENGTH = 500;

type FormState = 'initial' | 'active' | 'submitted' | 'error';

export const openLoginScreen = () => {
  triggerEvent(COMMENTS_TRIGGER_LOGIN);
};

type Props = {
  parentUrn?: string;
  threadUrn: string;
  isUserLoggedIn: boolean;
  isSrgLoginSupported: boolean;
  activeReplyForm: string | null;
};
export const ReplyForm = memo(
  ({
    threadUrn,
    parentUrn,
    isUserLoggedIn,
    isSrgLoginSupported = true,
    activeReplyForm,
  }: Props) => {
    const dispatch = useAppDispatcher();

    const loader = useLoader();
    const [text, setText] = useState('');
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
    const counter = MAX_COMMENT_LENGTH - text.length;

    const [formState, setFormState] = useState<FormState>(
      parentUrn ? 'active' : 'initial',
    );
    // form is active whenever user clicks or tabs to the textarea field
    const [isFormFocused, setIsFormFocused] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const textareaReference = useRef<HTMLTextAreaElement>(null);
    const errorMessageReference = useRef<HTMLDivElement>(null);

    const submitComment = async () => {
      try {
        setIsSubmitButtonDisabled(true);
        if (parentUrn) {
          await loader.saveReplyToComment(text.trim(), parentUrn);
        } else {
          await loader.saveComment(text.trim());
        }
        setFormState('submitted');
        setText('');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('error', error);
        setFormState('error');
        setErrorMessage(getServerUiMessage(error as ApiError));
      } finally {
        setIsSubmitButtonDisabled(false);
      }
    };

    const resetForm = () => {
      setText('');
      setErrorMessage('');
      setIsFormFocused(false);
      setFormState('initial');
    };

    const loginOrActivate = () => {
      if (!isSrgLoginSupported) {
        return;
      }

      if (isUserLoggedIn) {
        dispatch({
          type: 'update-active-reply-form',
          data: threadUrn,
        });
        setFormState('active');
      } else {
        dispatch({
          type: 'update-active-reply-form-after-login',
          data: threadUrn,
        });
        openLoginScreen();
      }
    };

    useEffect(() => {
      setIsSubmitButtonDisabled(
        text.length === 0 || text.length > MAX_COMMENT_LENGTH,
      );
    }, [text]);

    useEffect(() => {
      setText('');

      if (!parentUrn && activeReplyForm !== threadUrn) {
        setFormState('initial');
      } else if (!parentUrn && activeReplyForm === threadUrn) {
        setFormState('active');
      }
    }, [activeReplyForm, parentUrn, threadUrn]);

    useEffect(() => {
      let interval = 0;
      if (formState === 'submitted') {
        interval = window.setTimeout(() => {
          resetForm();
          dispatch({ type: 'update-active-reply-form', data: null });
        }, SUCCESS_MESSAGE_DISPLAY_DURATION);
      } else if (formState === 'active') {
        textareaReference?.current?.focus();
      } else if (formState === 'error') {
        errorMessageReference?.current?.focus();
      }
      return () => {
        if (interval) {
          window.clearInterval(interval);
        }
      };
    }, [dispatch, formState]);

    return (
      <div className="srf-comments-reply-form">
        {formState === 'initial' && (
          <>
            {!isSrgLoginSupported && <SrgLoginNotSupportedErrorMessage />}
            <button
              type="button"
              className="srf-comments-reply-form__initial"
              onClick={() => loginOrActivate()}>
              Teilen Sie Ihre Meinung...
              <span className="h-offscreen">
                anwählen um einen Kommentar zu schreiben
              </span>
            </button>
          </>
        )}
        {(formState === 'active' || formState === 'error') && (
          <>
            {formState === 'error' && (
              <ErrorMessage
                serverMessage={errorMessage}
                a11yRef={errorMessageReference}
              />
            )}
            <div
              className={classNames(
                'srf-comments-reply-form__textarea-container',
                {
                  'srf-comments-reply-form__textarea-container--active':
                    isFormFocused,
                },
              )}>
              <legend className="h-offscreen">Einen Kommentar schreiben</legend>
              <textarea
                ref={textareaReference}
                className="srf-comments-reply-form__textarea"
                rows={7}
                cols={70}
                placeholder={''}
                value={text}
                required={true}
                maxLength={1000}
                onChange={(event) => {
                  setText(event.target.value);
                }}
                onFocus={() => {
                  setIsFormFocused(true);
                }}
                onBlur={() => {
                  setIsFormFocused(false);
                }}
              />

              <p
                className={classNames(
                  'srf-comments-reply-form__counter-container',
                  {
                    'srf-comments-reply-form__counter-container__active':
                      isFormFocused,
                  },
                )}>
                <span
                  className={classNames('srf-comments-reply-form__counter', {
                    'srf-comments-reply-form__counter--warn': counter < 0,
                  })}>
                  <span className="h-offscreen">verfügbar sind noch</span>
                  {counter} Zeichen
                </span>
              </p>
            </div>
            <ConsentText />
            <SubmitButton
              disabled={isSubmitButtonDisabled}
              onClick={() => {
                submitComment();
              }}
            />
          </>
        )}
        {formState === 'submitted' && <SuccessMessage />}
      </div>
    );
  },
);

ReplyForm.displayName = 'ReplyForm';
